// Lib
import { ReactNode } from "react";
import dayjs from "dayjs";
// Types
import { SettingsResponseDto } from "types/settings";
import { DeliveryFeeSettings } from "types/coreSettings";
import { PaymentsSettingsResponseDto } from "types/paymentsSettings";
import {
  CustomerMenuAddonModifier,
  CustomerProfileResponseDto,
  GetCustomerMenuResponseDto,
  GetCustomersAddressResponseDto,
} from "types/customers";
import {
  CreateOrderRequestDto,
  OrderPaymentType,
  UserDeliveryAddressForm,
} from "types/orders";
import { Coupon, DiscountType } from "types/coupons";
import {
  Combos,
  CompleteProductData,
  MenuStatuses,
  SubtotalProductData,
  SummaryData,
  SummaryDataArgs,
} from "./types";
// Helpers
import { fixedDigitsValue, splitPhoneNumber } from "helpers/dataHelpers";
// Icons
import { CurrencyIcon } from "icons";
// Styled
import { Typography } from "styled/Typography";
import { FlexContainer } from "styled/Box";

import { CouponTypeContainer } from "./components/VoucherCode/styled";
import { getRoundedNumber } from "utils/getRoundedNumber";

const getExtraDeliveryFee = (
  totalOrderPrice: number,
  feeSettings?: DeliveryFeeSettings,
): number => {
  const orderPrice = totalOrderPrice / 1000;
  if (!feeSettings?.isVariableDeliveryFeeRangeAllowed) {
    return 0;
  }

  if (!feeSettings?.feeRanges?.length) {
    return 0;
  }

  // Find minimum and maximum values from the entire array
  const minValue = Math.min(
    ...(feeSettings?.feeRanges?.map(rate => rate.minOrderValue) || []),
  );
  const maxValue = Math.max(
    ...(feeSettings?.feeRanges?.map(rate => rate.maxOrderValue) || []),
  );

  // Find object with minimum minOrderValue to get its deliveryFee
  const minRate = feeSettings?.feeRanges?.reduce((min, current) =>
    current.minOrderValue < min.minOrderValue ? current : min,
  );

  // If order value is less than minimum value - return fee from minRate
  if (orderPrice < minValue) {
    return minRate.deliveryFee;
  }

  // If order value is greater than maximum value - return 0 (free delivery)
  if (orderPrice > maxValue) {
    return 0;
  }

  // Search in ranges for matching rate
  const rate = feeSettings?.feeRanges.find(
    rateValue =>
      orderPrice >= rateValue.minOrderValue &&
      orderPrice <= rateValue.maxOrderValue,
  );

  return rate ? rate.deliveryFee : 0;
};

export const getDeliveryFee = (
  settingsData: SettingsResponseDto,
  paymentSettingsData: PaymentsSettingsResponseDto,
  customerData: CustomerProfileResponseDto,
  extraDeliveryFeeFiat: number,
  extraDeliveryFeeCoins: number,
) => {
  if (!paymentSettingsData || !customerData) {
    return {
      fiatCentsPrice: 0,
      totalFmcCentsPrice: 0,
    };
  }
  const isCoinsExpired = dayjs(customerData.balanceData.expiresAt).isBefore(
    dayjs(),
  );

  const isDeliveryFee = settingsData.isDeliveryFeeAllowed;

  const deliveryFee = {
    fiatCentsPrice: paymentSettingsData?.deliveryFeeFiatCents || 0,
    totalFmcCentsPrice: 0,
  };

  if (isCoinsExpired && isDeliveryFee) {
    deliveryFee.totalFmcCentsPrice =
      paymentSettingsData?.deliveryFeeFmcCents || 0;
  }

  if (extraDeliveryFeeFiat) {
    deliveryFee.fiatCentsPrice =
      deliveryFee.fiatCentsPrice + extraDeliveryFeeFiat;
  }

  if (extraDeliveryFeeCoins) {
    deliveryFee.totalFmcCentsPrice =
      deliveryFee.totalFmcCentsPrice + extraDeliveryFeeCoins;
  }

  return deliveryFee;
};

export const getLoyaltyBonusPayload = (
  customerProfile?: CustomerProfileResponseDto | null,
): CreateOrderRequestDto["loyaltyBonus"] => {
  const { loyaltyData } = customerProfile || {};

  if (!loyaltyData?.bonus || !loyaltyData?.bonus?.isAvailable) {
    return null;
  }

  return {
    providerId: loyaltyData?.bonus?.providerId,
    title: loyaltyData?.bonus?.title,
    description: loyaltyData?.bonus?.description,
    imageUrl: loyaltyData?.bonus?.imageUrl,
    levelName: loyaltyData?.name,
  };
};

export const handleMutateOrderItems = (
  prev: CompleteProductData[],
  data: CompleteProductData,
): CompleteProductData[] => {
  const index = prev.findIndex(el => el.uniqId === data.uniqId);

  if (index === undefined) {
    return prev;
  }

  const newArray = [...prev];

  newArray[index] = data;

  return newArray;
};

export const getDiscountedPriceByPercents = (
  price: number,
  discountPercentages?: number,
) => {
  const percentages = discountPercentages ?? 0;

  const finalPrice = (price * 1000 * (1 - percentages / 100)) / 1000;

  return getRoundedNumber(finalPrice / 1000) * 1000;
};

const getAddonsTotalPrice = (addons: CustomerMenuAddonModifier[]) => {
  return addons.reduce(
    (acc, el) => {
      const quantity = el?.quantity || 1;

      const coins = el?.fmcCentsPrice ? el.fmcCentsPrice * quantity * 1000 : 0;

      const fiat = el?.fiatCentsPrice ? el.fiatCentsPrice * quantity * 1000 : 0;

      return {
        coins: acc.coins + coins,
        fiat: acc.fiat + fiat,
      };
    },
    { coins: 0, fiat: 0 },
  );
};

const getCombosTotalPrice = (combos: Combos) => {
  if (!combos || !Object.keys(combos)?.length) {
    return { coins: 0, fiat: 0 };
  }

  return Object.values(combos).reduce(
    (acc, el) => {
      const coins = el?.[0]?.fmcCentsPrice ? el[0].fmcCentsPrice * 1000 : 0;

      const fiat = el?.[0]?.fiatCentsPrice ? el[0].fiatCentsPrice * 1000 : 0;

      return {
        coins: acc.coins + coins,
        fiat: acc.fiat + fiat,
      };
    },
    { coins: 0, fiat: 0 },
  );
};

export const getSubtotal = ({
  product,
  addonsModifiers,
  quantity,
  combos,
}: CompleteProductData): SubtotalProductData => {
  const productPrice = {
    coins: product?.fmcCentsPrice ? product.fmcCentsPrice * 1000 : 0,
    fiat: product?.fiatCentsPrice ? product.fiatCentsPrice * 1000 : 0,
  };

  const combosTotalPrice = getCombosTotalPrice(combos);

  const addonsPrice = getAddonsTotalPrice(addonsModifiers.addon);

  const subTotalCoins = fixedDigitsValue(
    ((productPrice.coins + addonsPrice.coins + combosTotalPrice.coins) / 1000) *
      quantity,
    1,
  );

  const subTotalFiat = fixedDigitsValue(
    ((productPrice.fiat + addonsPrice.fiat + combosTotalPrice.fiat) / 1000) *
      quantity,
    3,
  );

  return { subTotalCoins, subTotalFiat };
};

export const renderSubtotal = (data: CompleteProductData): ReactNode => {
  const { subTotalCoins, subTotalFiat } = getSubtotal(data);
  return (
    <FlexContainer $gap={4} $align="center">
      <CurrencyIcon />

      <Typography.Title>
        {subTotalCoins} | KD {subTotalFiat}
      </Typography.Title>
    </FlexContainer>
  );
};

export const calculateSummary = ({
  coupons,
  orderItems,
  loyaltyData,
  settingsData,
  paymentSettingsData,
  customerData,
  coreSettings,
}: SummaryDataArgs): SummaryData => {
  const orderItemsData = orderItems.reduce(
    (acc, el) => {
      const productPrice = {
        coins: el.product?.fmcCentsPrice ? el.product.fmcCentsPrice * 1000 : 0,
        fiat: el.product?.fiatCentsPrice ? el.product.fiatCentsPrice * 1000 : 0,
      };

      const addonsPrice = getAddonsTotalPrice(el.addonsModifiers.addon);

      const combosPrice = getCombosTotalPrice(el.combos);

      let subTotalCoins =
        (productPrice.coins + addonsPrice.coins + combosPrice.coins) *
        el.quantity;

      let subTotalFiat =
        (productPrice.fiat + addonsPrice.fiat + combosPrice.fiat) * el.quantity;

      const originalTotalCoins = subTotalCoins;
      const originalTotalFiat = subTotalFiat;

      let loyaltyDiscountCoins = 0;
      let loyaltyDiscountFiat = 0;

      if (loyaltyData) {
        subTotalCoins = getDiscountedPriceByPercents(
          subTotalCoins,
          loyaltyData.discountPercentage,
        );

        subTotalFiat = getDiscountedPriceByPercents(
          subTotalFiat,
          loyaltyData.discountPercentage,
        );

        loyaltyDiscountCoins = subTotalCoins
          ? originalTotalCoins - subTotalCoins
          : 0;

        loyaltyDiscountFiat = subTotalFiat
          ? originalTotalFiat - subTotalFiat
          : 0;
      }

      return {
        quantity: acc.quantity + el.quantity,
        subTotal: {
          coins: acc.subTotal.coins + subTotalCoins,
          fiat: acc.subTotal.fiat + subTotalFiat,
        },
        loyaltyDiscount: {
          coins: acc.loyaltyDiscount.coins + loyaltyDiscountCoins,
          fiat: acc.loyaltyDiscount.fiat + loyaltyDiscountFiat,
        },
        originalTotal: {
          coins: acc.originalTotal.coins + originalTotalCoins,
          fiat: acc.originalTotal.fiat + originalTotalFiat,
        },
      };
    },
    {
      quantity: 0,
      subTotal: { coins: 0, fiat: 0 },
      loyaltyDiscount: { coins: 0, fiat: 0 },
      originalTotal: {
        coins: 0,
        fiat: 0,
      },
    },
  );

  const percentageCoupons = coupons.filter(
    c => c.discountType === DiscountType.Percentage,
  );

  const fixedValuesCoupons = coupons.filter(
    c => c.discountType !== DiscountType.Percentage,
  );

  const couponsDiscount = {};

  const discountedTotal = {
    coins: orderItemsData.subTotal.coins,
    fiat: orderItemsData.subTotal.fiat,
  };

  fixedValuesCoupons.forEach(c => {
    const coins =
      orderItemsData.subTotal.coins === 0
        ? 0
        : orderItemsData.subTotal.coins < c.minimumOrderFmcCentsPrice * 1000
        ? 0
        : c.discountType === DiscountType.FmcCents
        ? c.discountOff * 1000
        : 0;

    const fiat =
      orderItemsData.subTotal.coins === 0
        ? 0
        : orderItemsData.subTotal.fiat < c.minimumOrderFiatCentsPrice * 1000
        ? 0
        : c.discountType === DiscountType.FiatCents
        ? c.discountOff * 1000
        : 0;

    discountedTotal.coins -= coins;

    discountedTotal.fiat -= fiat;

    couponsDiscount[c.id] = {
      coins: coins / 1000,
      fiat: fiat / 1000,
    };
  });

  percentageCoupons.forEach(c => {
    const coins =
      orderItemsData.subTotal.coins === 0
        ? 0
        : orderItemsData.subTotal.coins < c.minimumOrderFmcCentsPrice * 1000
        ? 0
        : getDiscountedPriceByPercents(discountedTotal.coins, c.discountOff);

    const fiat =
      orderItemsData.subTotal.coins === 0
        ? 0
        : orderItemsData.subTotal.fiat < c.minimumOrderFiatCentsPrice * 1000
        ? 0
        : getDiscountedPriceByPercents(discountedTotal.fiat, c.discountOff);

    discountedTotal.coins -= coins;

    discountedTotal.fiat -= fiat;

    couponsDiscount[c.id] = {
      coins: coins / 1000,
      fiat: fiat / 1000,
    };
  });

  const discountedTotalCoins =
    discountedTotal.coins > 0 ? discountedTotal.coins : 0;

  const discountedTotalFiat =
    discountedTotal.fiat > 0 ? discountedTotal.fiat : 0;

  const extraDeliveryFeeFiat = getExtraDeliveryFee(
    discountedTotalFiat,
    coreSettings?.deliveryFeeSettings,
  );
  const extraDeliveryFeeCoins = getExtraDeliveryFee(
    discountedTotalCoins,
    coreSettings?.deliveryFeeSettings,
  );

  const deliveryFee = getDeliveryFee(
    settingsData,
    paymentSettingsData,
    customerData,
    extraDeliveryFeeFiat,
    extraDeliveryFeeCoins,
  );

  return {
    quantity: orderItemsData.quantity || 0,
    subTotal: {
      coins: getRoundedNumber(discountedTotalCoins / 1000),
      fiat: getRoundedNumber(discountedTotalFiat / 1000),
    },
    deliveryFee: {
      coins: deliveryFee.totalFmcCentsPrice,
      fiat: deliveryFee.fiatCentsPrice,
    },
    total: {
      coins:
        (discountedTotalCoins + deliveryFee.totalFmcCentsPrice * 1000) / 1000,

      fiat: (discountedTotalFiat + deliveryFee.fiatCentsPrice * 1000) / 1000,
    },
    couponsDiscount,
    loyaltyDiscount: {
      coins: getRoundedNumber(orderItemsData.loyaltyDiscount.coins / 1000),
      fiat: getRoundedNumber(orderItemsData.loyaltyDiscount.fiat / 1000),
    },
    originalTotal: {
      coins: getRoundedNumber(orderItemsData.originalTotal.coins / 1000),
      fiat: getRoundedNumber(orderItemsData.originalTotal.fiat / 1000),
    },
  };
};

export const renderDiscountInfo = (coupon: Coupon) => {
  const getType = () => {
    switch (coupon.discountType) {
      case DiscountType.Percentage:
        return {
          renderValue: <Typography.Title>%</Typography.Title>,
          value: "%",
        };
      case DiscountType.FiatCents:
        return {
          renderValue: <Typography.Title>KD</Typography.Title>,
          value: " KWD",
        };
      case DiscountType.FmcCents:
        return {
          renderValue: <CurrencyIcon width="24" height="24" />,
          value: " FM Coins",
        };

      default:
        break;
    }
  };

  const { minimumOrderFiatCentsPrice, minimumOrderFmcCentsPrice } = coupon;

  const { value, renderValue } = getType();

  const minOrderDescription = `${
    minimumOrderFmcCentsPrice
      ? `${fixedDigitsValue(minimumOrderFmcCentsPrice, 1)} FM Coins`
      : ""
  }${minimumOrderFmcCentsPrice && minimumOrderFiatCentsPrice ? " | " : ""}${
    minimumOrderFiatCentsPrice
      ? `KD ${fixedDigitsValue(minimumOrderFiatCentsPrice, 3)}`
      : ""
  }`;

  return (
    <FlexContainer $gap={12} $align="center">
      <CouponTypeContainer>{renderValue}</CouponTypeContainer>

      <div>
        <Typography.Description>Voucher applied</Typography.Description>

        <Typography.Title>{`${coupon.discountOff}${value} off`}</Typography.Title>

        {minOrderDescription && (
          <Typography.DescriptionThin>
            Min Purchase: {minOrderDescription}
          </Typography.DescriptionThin>
        )}
      </div>
    </FlexContainer>
  );
};

const transformDeliveryAddressesToFormValues = (
  address: GetCustomersAddressResponseDto,
): UserDeliveryAddressForm => {
  const { phone, phoneCode } = splitPhoneNumber(address.phone);
  return {
    id: address.id,
    name: address.name,
    city: address.city,
    area: address.area,
    block: address.block,
    street: address.street,
    building: address.building,
    floor: address.floor,
    flat: address.flat,
    additionalInfo: address.additionalInfo,
    isSelected: address?.isSelected,
    phoneCode,
    phone,
  };
};

export const getDeliveryAddresses = (
  deliveryAddresses: GetCustomersAddressResponseDto[],
): {
  defaultDeliveryAddress: UserDeliveryAddressForm | null;
  deliveryAddressesList: UserDeliveryAddressForm[];
} => {
  if (!deliveryAddresses || !deliveryAddresses?.length) {
    return {
      defaultDeliveryAddress: null,
      deliveryAddressesList: [],
    };
  }

  const deliveryAddressesList = deliveryAddresses?.map(a =>
    transformDeliveryAddressesToFormValues(a),
  );

  const defaultDeliveryAddress =
    deliveryAddressesList?.find(a => a.isSelected) || null;

  return {
    defaultDeliveryAddress,
    deliveryAddressesList,
  };
};

export const getMenuStatuses = (
  menu: GetCustomerMenuResponseDto,
): MenuStatuses => {
  if (!menu) {
    return {
      isBusy: false,
      isClosed: true,
      isMenuCashPaymentAvailable: false,
    };
  }

  const isBusy = menu?.locationOperatingStatus === "paused";

  const isClosed = menu.isAvailable === false;

  const isMenuCashPaymentAvailable =
    (menu?.isCashPaymentAvailable && menu?.isCashPayment) || false;

  return {
    isBusy,
    isClosed,
    isMenuCashPaymentAvailable,
  };
};

export const checkIfCashPaymentsAvailable = (
  locationId: string,
  paymentSettingsData: PaymentsSettingsResponseDto,
  menuStatuses: MenuStatuses,
): boolean => {
  if (!paymentSettingsData) {
    return false;
  }

  if (!locationId) {
    return false;
  }

  if (!menuStatuses.isMenuCashPaymentAvailable) {
    return false;
  }

  if (!paymentSettingsData.availableLocations?.length) {
    return false;
  }

  if (!paymentSettingsData.availableLocations?.find(el => el === locationId)) {
    return false;
  }

  return true;
};

export const checkIfCoinsExpiredError = (
  paymentType: OrderPaymentType,
  settingsData: SettingsResponseDto,
  customerData: CustomerProfileResponseDto,
): boolean => {
  if (!customerData) {
    return false;
  }

  if (settingsData?.isDeliveryFeeAllowed) {
    return false;
  }

  if (!customerData?.balanceData?.expiresAt) {
    return false;
  }

  const isBalanceExpired = dayjs(customerData.balanceData.expiresAt).isBefore(
    dayjs(),
  );

  if (paymentType === OrderPaymentType.FmcCents && isBalanceExpired) {
    return true;
  }

  return false;
};

export const convertCombosToArray = (
  combos: Combos,
): {
  productId: string;
  quantity: number;
  fmcCentsPrice: number;
  fiatCentsPrice: number;
}[] => {
  if (!combos) return [];

  return Object.values(combos)
    .flat()
    .map(item => ({
      productId: item.id,
      quantity: 1,
      fmcCentsPrice: item.fmcCentsPrice,
      fiatCentsPrice: item.fiatCentsPrice,
    }));
};
